/**
 * rain_shelter — THE ROOF MAP (render wave 1, 2026-09-17) and, since the
 * shader gaps program's round 3 (weather surfaces, 2026-09-30), the WIND'S
 * SHELTER and the GROUND'S MEMORY in one texture.
 *
 * Rain and snow used to fall through every porch, awning, hangar mouth, cave
 * mouth and tree canopy, and the ground under them wetted and puddled like
 * the open street, because shelter was one flag (an interior is active) and
 * wetness one global uniform. After Lagarde's "Water drop 2a" (2012): render
 * a depth map "from the top in the direction of the rain", then test each
 * drop and each ground fragment against it.
 *
 * THE WIND'S SHELTER (2026-09-30). The map used to be baked straight down, so
 * in a gale the drops fell slanted (the emitters lean them) while the dry
 * patch stayed straight under the roof. The bake is now SHEARED along the
 * precipitation: a drop travels along (sx, −1, sz) — `precipShear`, the SAME
 * lean the emitters use (`precipSlant`, one source of truth) — so every rain
 * LINE p + t·(−sx, 1, −sz) is one texel, addressed by where it crosses the
 * reference height yRef: (x + sx·(y − yRef), z + sz·(y − yRef)). The
 * orthographic camera's matrix carries that shear (`shelterViewMatrix`), the
 * depth test keeps the HIGHEST occluder on each line, and the texel still
 * stores the roof's ABSOLUTE height — so a consumer compares heights exactly
 * as before: every existing consumer (the ground, the GPU drops' size gate,
 * the ripples mask) goes through `shelterExposure` and needed no change. With
 * no wind the shear is zero and the map is the old vertical one, bit for bit.
 * The shear EASES (`easeShear`, a rate limit) and is HELD while nothing falls
 * (the ground still drying under a roof keeps the patch the rain left).
 *
 * THE ATLAS. One 512 × 1025 RGBA16F target, two regions, one binding — the
 * v2 ground is at its sampled-texture limit (agent B counted ~16), so the
 * deformation field could not be a texture of its own:
 *   • ROOF   (viewport 0,1 — rows 1…512 from the top): R = roof ABSOLUTE height, A = 1
 *     where anything drew; baked by RainShelterModule.
 *   • DEFORM (viewport 0,513 — rows 513…1024): R = the snow / mud
 *     depression depth (m) of the camera-following deformation field
 *     (`lib/deform_field.ts`, the engine's DeformationHeightmap), in its OWN
 *     window (unsheared, finer). Written by a separate small bake; the roof
 *     bake never clears it (its "clear" is a quad over its own viewport and a
 *     depth-only clear — a WebGPU colour clear is a loadOp over the WHOLE
 *     attachment).
 * three's viewport is TOP-LEFT on both backends (WebGL2 flips it), and a
 * render target samples TOP-LEFT on both too (WebGL2 flips the uv — THE TOP-LEFT
 * LAW, `shelterTexelUv`). A one-row gutter on top keeps every region's viewport from ever EQUALLING a
 * canvas (THE VIEWPORT QUIRK, `SHELTER_ATLAS_H`). Readbacks are NOT flipped: WebGL2
 * reads rows bottom-up, WebGPU top-down (`roofReadRect`, `roofRowToMapV`).
 *
 * THE FRAME. The world is 2,048 km under an FP64 origin rebase: children keep
 * ABSOLUTE coordinates and `scene.position` is −origin, so a `positionWorld`
 * read in a graph is RENDER space. The window's centre is kept in absolute
 * metres and the bounds uniform is refreshed every frame in render space
 * (`shelterBoundsRender`), so a rebase between two bakes cannot shift the
 * map under the world. The map stores the roof's ABSOLUTE height (render y
 * minus `scene.position.y` at bake) and the consumer compares against the
 * fragment's absolute height the same way — `shelterBounds.w` carries the
 * CURRENT `scene.position.y`; the shear's yRef is absolute too.
 *
 * WHAT IS A ROOF. Anything on `RAIN_SHELTER_LAYER`: the static instancers'
 * batches (settlements, structures, world objects, decorations, admin
 * placements), the chunk-foliage batches that cast shadows (trees and rocks,
 * never groundcover — a grass blade must not dry the ground), the cave rock,
 * and (2026-09-30) proxy discs for the PARKED ships (the module places them
 * from the vehicle module's public `parkedShips()`; they draw only in the
 * bake). Terrain, water, sky, particles, characters and moving ships never.
 *
 * The producer is `modules/RainShelterModule.ts`; the consumers are the ground
 * shader (wetness, puddles, ripples, snow accumulation, the deformation
 * imprint), the GPU weather emitters (a sheltered drop collapses to nothing
 * through the material's size gate), the WebGL2 CPU drops (a readback of the
 * roof half → `gateCpuDrops`) and the prop/structure wet layer (sampled in
 * the VERTEX stage — no fragment sampler). `verify:rain-shelter` and
 * `verify:weather-surfaces` pin the pure half; the bed pins the bake.
 */
import * as THREE from 'three/webgpu';
import { Fn, uniform, texture as tslTexture, float, vec2, smoothstep, select, clamp } from 'three/tsl';

/** Side length (m) of the window around the player. Past the near rain box and the groundcover ring. */
export const SHELTER_REGION_M = 96;
/** Re-centre once the player is this far from the middle. */
export const SHELTER_RECENTER_M = SHELTER_REGION_M * 0.25;
/** Map resolution (texels a side): 0.19 m a texel. */
export const SHELTER_RES = 512;
/** The bake camera sits this far above the player and sees this far down. */
export const SHELTER_CAM_ABOVE_M = 400;
export const SHELTER_CAM_FAR_M = 800;
/** Re-bake this often while it rains, snows or the ground is still drying. */
export const SHELTER_REBAKE_S = 2;
/** The scene layer a roof lives on (Atlas uses no other layer). */
export const RAIN_SHELTER_LAYER = 7;
/** A roof this little above a point starts to shelter it; this much above shelters it fully. */
export const SHELTER_OVER_MIN_M = 0.1;
export const SHELTER_OVER_MAX_M = 0.6;

// ── the atlas: roof over deformation, one binding ──
/**
 * The target's height in texels: a one-row GUTTER on top, the roof region (SHELTER_RES²), the deformation region
 * (SHELTER_RES²). THE VIEWPORT QUIRK (three r185 `Renderer._renderScene`): a render target's viewport that EQUALS the
 * CANVAS drawing buffer `(0, 0, w, h)` is treated as "no viewport" and the whole target is drawn — a (0, 0, 512, 512)
 * region on a 512 × 512 canvas painted the whole atlas (the bed found it). No region starts at y = 0, so no region can
 * ever equal a canvas. (Also: a target's `scissorTest` is ignored — the scissor ENABLE is the canvas's; the regions
 * need none, triangles are clipped to their viewport.)
 */
export const SHELTER_ATLAS_H = SHELTER_RES * 2 + 1;
/** The roof region's viewport (three's TOP-LEFT convention on both backends) and the deformation region's. */
export const ROOF_VIEWPORT: readonly [number, number, number, number] = [0, 1, SHELTER_RES, SHELTER_RES];
export const DEFORM_VIEWPORT: readonly [number, number, number, number] = [0, SHELTER_RES + 1, SHELTER_RES, SHELTER_RES];
/** Half a texel of the atlas in v: the regions' uv is clamped this far inside so bilinear taps never cross the seam. */
const HALF_TEXEL_V = 0.5 / SHELTER_ATLAS_H;

// ── THE WIND LEAN — one source of truth (2026-09-30) ──
/** Downwind drift per metre of fall at full wind: rain a little, snow a lot (WeatherModule's emitters lean by this). */
export const PRECIP_LEAN = { rain: 0.28, snow: 0.55 } as const;
/** How fast the baked shear may follow the wind (per second): a full reversal of a rain gale (0.56) glides over ~19 s. */
export const SHEAR_EASE_PER_S = 0.03;
/** Re-bake at once (throttled) when the eased shear has moved this far from the baked one. */
export const SHEAR_REBAKE_EPS = 0.02;

// ── pure ──

/**
 * THE ROOF-INDOORS LAW (2026-09-22): the map stays ON inside a BUILDING —
 * the interior shell puts its slabs and roof on the layer, so the rain stops
 * at the ceiling while the street outside the panes still pours (the exterior
 * is hidden; the map sees the shell). It stays OFF in any other interior
 * regime (a sky-ship's deck has no shell on the layer — the weather's own
 * muffle handles it) exactly as before.
 */
export function shelterActiveIndoors(interiorId: string | null, shellOnLayer: boolean): boolean {
  return interiorId === null || shellOnLayer;
}

export function shelterNeedsRecenter(centerX: number, centerZ: number, px: number, pz: number): boolean {
  return Math.abs(px - centerX) > SHELTER_RECENTER_M || Math.abs(pz - centerZ) > SHELTER_RECENTER_M;
}

/** The window's render-space bounds (minX, minZ, size) from its absolute centre and the current origin. */
export function shelterBoundsRender(centerAbsX: number, centerAbsZ: number, originX: number, originZ: number, size = SHELTER_REGION_M): [number, number, number] {
  return [centerAbsX - originX - size / 2, centerAbsZ - originZ - size / 2, size];
}

/** Map uv of a render-space point, or null outside the window. */
export function shelterUv(minX: number, minZ: number, size: number, x: number, z: number): [number, number] | null {
  const u = (x - minX) / size, v = (z - minZ) / size;
  if (u < 0 || u > 1 || v < 0 || v > 1) return null;
  return [u, v];
}

/**
 * Map uv → texel uv WITHIN THE ROOF REGION, in three's SAMPLING convention. THE TOP-LEFT LAW (2026-09-30, found by
 * the weather-surfaces bed): three r185 samples a RENDER TARGET with v = 0 at the TOP of the rendered image on BOTH
 * backends — WGSL natively, GLSL by flipping (`GLSLNodeBuilder.isFlipY()` + TextureNode's flip uniform, true for any
 * `isRenderTargetTexture`). The bake camera's +Y is world −Z, so the region's top row is the window's −Z edge: the
 * texel v IS the map v. (Until 2026-09-30 this returned 1 − v — "three keeps a bottom-left uv origin for render
 * targets" — and every consumer read the roof map MIRRORED about the window's z centre; the old probe read the map
 * back by rows, which does not go through the sampler, and never saw it.)
 */
export function shelterTexelUv(u: number, v: number): [number, number] { return [u, v]; }
/** The atlas v where the roof region begins (under the gutter row) and a region's height in v. */
export const ROOF_V0 = 1 / SHELTER_ATLAS_H;
export const REGION_VH = SHELTER_RES / SHELTER_ATLAS_H;
/** The deformation region begins under the roof region. */
export const DEFORM_V0 = (SHELTER_RES + 1) / SHELTER_ATLAS_H;
/** Map v (after `shelterTexelUv`) → the atlas's v for the ROOF region, clamped half a texel off its edges. */
export function roofAtlasV(vMap: number): number { return Math.min(ROOF_V0 + REGION_VH - HALF_TEXEL_V, Math.max(ROOF_V0 + HALF_TEXEL_V, ROOF_V0 + REGION_VH * vMap)); }
/** …and for the DEFORMATION region (under the roof region). */
export function deformAtlasV(vMap: number): number { return Math.min(DEFORM_V0 + REGION_VH - HALF_TEXEL_V, Math.max(DEFORM_V0 + HALF_TEXEL_V, DEFORM_V0 + REGION_VH * vMap)); }

/**
 * The roof region as `readRenderTargetPixelsAsync` wants it: WebGL2 reads
 * rows from the BOTTOM (the region is rows 512…1023 over the deformation's
 * 0…511), WebGPU copies rows from the TOP (rows 1…512, under the gutter). THE
 * READBACK LAW (damage agent): the two backends disagree on the row order,
 * never on the uv.
 */
export function roofReadRect(webgpu: boolean): [number, number, number, number] {
  return webgpu ? [0, 1, SHELTER_RES, SHELTER_RES] : [0, SHELTER_RES, SHELTER_RES, SHELTER_RES];
}
/** Row k of a roof-region readback → the map's v (0 = the window's −Z edge … 1 = +Z) at that row's centre. */
export function roofRowToMapV(k: number, webgpu: boolean): number {
  const vLocal = webgpu ? 1 - (k + 0.5) / SHELTER_RES : (k + 0.5) / SHELTER_RES;
  return 1 - vLocal;
}

const smooth01 = (t: number): number => { const s = t < 0 ? 0 : t > 1 ? 1 : t; return s * s * (3 - 2 * s); };

/**
 * Exposure of a point (1 in the open, 0 under a roof) from the map's sample:
 * `has` is the texel's alpha (1 where anything drew, filtered), `roofY` the
 * roof's absolute height it holds, `absY` the point's absolute height.
 */
export function shelterExposureCpu(has: number, roofY: number, absY: number): number {
  const over = roofY - absY;
  return 1 - has * smooth01((over - SHELTER_OVER_MIN_M) / (SHELTER_OVER_MAX_M - SHELTER_OVER_MIN_M));
}

/** Downwind drift per metre of fall of a precipitation kind at wind strength 0..1 (0 for anything that does not fall). */
export function precipSlant(kind: string, windStrength01: number): number {
  const k = kind === 'snow' ? PRECIP_LEAN.snow : kind === 'rain' ? PRECIP_LEAN.rain : 0;
  const s = windStrength01 < 0 ? 0 : windStrength01 > 1 ? 1 : windStrength01;
  return k * s;
}
/** The shear (sx, sz): the precipitation travels along (sx, −1, sz) — the wind's unit xz times the slant. */
export function precipShear(kind: string, windX: number, windZ: number, windStrength01: number): [number, number] {
  const l = Math.hypot(windX, windZ);
  if (!(l > 1e-9)) return [0, 0];
  const s = precipSlant(kind, windStrength01);
  return [(windX / l) * s, (windZ / l) * s];
}
/** Move the baked shear toward the target by at most SHEAR_EASE_PER_S·dt per axis-length (a glide, never a jump). */
export function easeShear(cur: readonly [number, number], target: readonly [number, number], dtS: number): [number, number] {
  const dx = target[0] - cur[0], dz = target[1] - cur[1];
  const d = Math.hypot(dx, dz), step = SHEAR_EASE_PER_S * Math.max(0, dtS);
  if (d <= step || d < 1e-12) return [target[0], target[1]];
  return [cur[0] + (dx / d) * step, cur[1] + (dz / d) * step];
}

/**
 * The bake camera's VIEW matrix (column-major, three's `elements` order):
 *   X_v = (x − cx) + sx·(y − yRef)
 *   Y_v = −((z − cz) + sz·(y − yRef))      (the camera's +Y is world −Z — `shelterTexelUv`'s flip)
 *   Z_v = y − camY                          (it looks down −Z_v; the highest occluder on a line is nearest)
 * All in RENDER space. With sx = sz = 0 it is the old straight-down camera (rotation −90° about X at (cx, camY, cz)).
 * The module seats it with `seatViewMatrix` (never `updateMatrixWorld`, which drops the shear), so three's
 * `matrixWorldInverse` IS this.
 */
export function shelterViewMatrix(cx: number, cz: number, yRef: number, camY: number, sx: number, sz: number): number[] {
  // rows: [1, sx, 0, −cx − sx·yRef], [0, −sz, −1, cz + sz·yRef], [0, 1, 0, −camY], [0, 0, 0, 1]
  return [
    1, 0, 0, 0,
    sx, -sz, 1, 0,
    0, -1, 0, 0,
    -cx - sx * yRef, cz + sz * yRef, -camY, 1,
  ];
}

/**
 * Seat a view matrix on a camera EXACTLY. THE CAMERA-SCALE LAW (2026-09-30, found by the weather-surfaces bed): three
 * r185's `Camera.updateMatrixWorld` decomposes the world matrix and, when its scale is not exactly 1, re-composes it
 * WITHOUT the scale ("to be glTF conform") — a SHEARED view decomposes into a garbage rotation, and every windy bake
 * rendered the roof 70 m off the map (nothing at all). So the view and its inverse are written directly and the
 * renderer is told never to update them (`matrixWorldAutoUpdate = false`: `Renderer.render` then skips its
 * `camera.updateMatrixWorld()`). Never call `updateMatrixWorld` on a camera seated this way.
 */
export function seatViewMatrix(cam: THREE.Camera, view: THREE.Matrix4): void {
  cam.matrixAutoUpdate = false;
  cam.matrixWorldAutoUpdate = false;
  cam.matrix.copy(view).invert();
  cam.matrixWorld.copy(cam.matrix);
  cam.matrixWorldInverse.copy(view);
}

/** The sheared map uv of a point: (x, z) moved along its rain line to the reference height, then the window's uv. */
export function shelterShearedUv(minX: number, minZ: number, size: number, sx: number, sz: number, yRef: number, x: number, y: number, z: number): [number, number] | null {
  return shelterUv(minX, minZ, size, x + sx * (y - yRef), z + sz * (y - yRef));
}

// ── the CPU copy of the roof (the WebGL2 drops' gate, the probes) ──

/** A readback of the roof region re-ordered to the MAP's rows: texel (i, j) ↔ u = (i + ½)/RES, v = (j + ½)/RES. All ABSOLUTE. */
export interface ShelterCpuGrid {
  roof: Float32Array;
  has: Float32Array;
  /** the window's ABSOLUTE min corner and size at bake */
  minX: number; minZ: number; size: number;
  /** the shear the map was baked with, and its ABSOLUTE reference height */
  sx: number; sz: number; yRef: number;
  res: number;
  /** performance.now() of the bake this copies */
  bakedAt: number;
}

/** Re-order a roof-region readback (RGBA, row order by backend) into a grid in map rows. */
export function gridFromReadback(buf: ArrayLike<number>, half: boolean, webgpu: boolean, meta: Omit<ShelterCpuGrid, 'roof' | 'has' | 'res'>, decodeHalf: (h: number) => number): ShelterCpuGrid {
  const R = SHELTER_RES;
  const roof = new Float32Array(R * R), has = new Float32Array(R * R);
  for (let k = 0; k < R; k++) {
    const v = roofRowToMapV(k, webgpu);
    const j = Math.min(R - 1, Math.max(0, Math.floor(v * R)));
    for (let i = 0; i < R; i++) {
      const o = (k * R + i) * 4;
      roof[j * R + i] = half ? decodeHalf(buf[o]) : buf[o];
      has[j * R + i] = half ? decodeHalf(buf[o + 3]) : buf[o + 3];
    }
  }
  return { roof, has, res: R, ...meta };
}

/** Exposure of an ABSOLUTE point from the CPU grid (nearest texel; 1 outside the window or with no grid). */
export function shelterExposureFromGrid(g: ShelterCpuGrid | null, absX: number, absY: number, absZ: number): number {
  if (!g) return 1;
  const uv = shelterShearedUv(g.minX, g.minZ, g.size, g.sx, g.sz, g.yRef, absX, absY, absZ);
  if (!uv) return 1;
  const i = Math.min(g.res - 1, Math.floor(uv[0] * g.res)), j = Math.min(g.res - 1, Math.floor(uv[1] * g.res));
  return shelterExposureCpu(g.has[j * g.res + i], g.roof[j * g.res + i], absY);
}

/** The engine CPU emitter's layout (enginex/particles/types PARTICLE_STRIDE): position 0–2, age 6, lifetime 7, size 8. */
export const CPU_PARTICLE_STRIDE = 16;
/**
 * THE WEBGL2 DROPS' GATE: the CPU adapter cannot run the GPU size gate, so after each update every live drop is
 * looked up in the CPU copy of the roof (its positions are ABSOLUTE — the FRAME LAW) and a sheltered one is
 * collapsed (size 0 this frame) and aged out (gone at the next update). Returns how many were gated.
 */
export function gateCpuDrops(data: Float32Array, active: number, g: ShelterCpuGrid | null, stride = CPU_PARTICLE_STRIDE): number {
  if (!g) return 0;
  let n = 0;
  for (let p = 0; p < active; p++) {
    const o = p * stride;
    if (shelterExposureFromGrid(g, data[o], data[o + 1], data[o + 2]) < 0.5) {
      data[o + 8] = 0;
      data[o + 6] = data[o + 7];
      n++;
    }
  }
  return n;
}

/** The drops gated on the CPU backend (diagnostics — `__shelter().cpuGate`). */
export const cpuGateStats = { frames: 0, gated: 0, lastGated: 0 };
interface CpuEmitterLike { backend?: string; emitter?: { data: Float32Array; activeCount: number } }
/** Gate every CPU-backend emitter against the CPU copy of the roof (a no-op without a live map or a copy). */
export function gateCpuEmitters(list: ReadonlyArray<CpuEmitterLike | null | undefined>): number {
  if (!cpuDropGateLive()) return 0;
  let n = 0;
  for (const e of list) if (e && e.backend === 'cpu' && e.emitter) n += gateCpuDrops(e.emitter.data, e.emitter.activeCount, cpuGrid);
  cpuGateStats.frames++; cpuGateStats.gated += n; cpuGateStats.lastGated = n;
  return n;
}
/** The CPU drop gate can answer: the map is live and a copy of it has landed. */
export function cpuDropGateLive(): boolean {
  return cpuGrid !== null && (shelterEnabled as unknown as { value: number }).value === 1;
}

// ── shared state (the producer writes, the consumers read) ──

/** (minX, minZ, size, sceneY) in RENDER space — refreshed every frame by the module. */
export const shelterBounds = new THREE.Vector4(-SHELTER_REGION_M / 2, -SHELTER_REGION_M / 2, SHELTER_REGION_M, 0);
export const shelterBoundsNode = uniform(shelterBounds);
/** (sx, sz, yRef ABSOLUTE, 0): the shear the CURRENT map was baked with — written at bake, with the map. */
export const shelterShear = new THREE.Vector4(0, 0, 0, 0);
export const shelterShearNode = uniform(shelterShear);
/** 1 while the map is live (flag on, on a world, no interior active), else every point is exposed. */
export const shelterEnabled = uniform(0);
/** The deformation region's window: (minX, minZ) in RENDER space, the view size (m) and the cell (m) — the module writes it. */
export const deformBounds = new THREE.Vector4(-19, -19, 38, 0.15);
export const deformBoundsNode = uniform(deformBounds);
/** 1 while the deformation region holds a live field. */
export const deformEnabled = uniform(0);

let target: THREE.RenderTarget | null = null;
let texNode: ReturnType<typeof tslTexture> | null = null;
/** The CPU copy of the roof region (WebGL2's drop gate; the probes). The module replaces it after each readback. */
let cpuGrid: ShelterCpuGrid | null = null;
export function setShelterCpuGrid(g: ShelterCpuGrid | null): void { cpuGrid = g; }
export function shelterCpuGrid(): ShelterCpuGrid | null { return cpuGrid; }

/**
 * The atlas render target — ONE object for the session, created on first
 * use, because TSL binds the texture OBJECT at graph build: the ground material
 * built before the module's first bake must already sample the real target.
 */
export function ensureShelterTarget(): THREE.RenderTarget {
  if (target) return target;
  target = new THREE.RenderTarget(SHELTER_RES, SHELTER_ATLAS_H, {
    type: THREE.HalfFloatType, format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    depthBuffer: true, generateMipmaps: false,
  });
  target.texture.name = 'rain-shelter';
  return target;
}

/** The atlas as a TSL texture node (one node, reused by every consumer — every sample of it is ONE binding). */
export function shelterTextureNode(): ReturnType<typeof tslTexture> {
  if (!texNode) texNode = tslTexture(ensureShelterTarget().texture);
  return texNode;
}

type F = ReturnType<typeof float>;
type V4 = { x: F; y: F; z: F; w: F };

/**
 * Exposure of a RENDER-space position: 1 in the open (and outside the window,
 * and while the map is off), 0 under a roof, a smoothstep between. The TSL of
 * `shelterExposureCpu` over `shelterShearedUv`. Explicit LOD 0 (the target has
 * no mips) — legal in the vertex stage and in non-uniform flow.
 */
export const shelterExposure = Fn(([wp]: [unknown]) => {
  const p = wp as unknown as { x: F; y: F; z: F };
  const b = shelterBoundsNode as unknown as V4;
  const sh = shelterShearNode as unknown as V4;
  const lift = p.y.sub(b.w).sub(sh.z); // the point's ABSOLUTE height above the shear's reference
  const u = p.x.add(sh.x.mul(lift)).sub(b.x).div(b.z), v = p.z.add(sh.y.mul(lift)).sub(b.y).div(b.z);
  const inside = u.greaterThanEqual(0).and(u.lessThanEqual(1)).and(v.greaterThanEqual(0)).and(v.lessThanEqual(1));
  // roofAtlasV(v): THE TOP-LEFT LAW — the region's top row is the window's −Z edge (v 0)
  const vA = clamp(float(ROOF_V0).add(v.mul(REGION_VH)), ROOF_V0 + HALF_TEXEL_V, ROOF_V0 + REGION_VH - HALF_TEXEL_V);
  const s = (shelterTextureNode().sample(vec2(u, vA) as never) as unknown as { level: (l: unknown) => { r: F; a: F } }).level(float(0));
  const over = s.r.sub(p.y.sub(b.w));
  const sheltered = s.a.mul(smoothstep(float(SHELTER_OVER_MIN_M), float(SHELTER_OVER_MAX_M), over));
  const live = inside.and((shelterEnabled as unknown as F).greaterThan(0.5));
  return select(live, float(1).sub(sheltered), float(1));
});

/**
 * The deformation depth (m, ≥ 0) at a RENDER-space xz from the atlas's lower region, bilinear, LOD 0 — and 0 outside
 * the field's window or while it is off. `offX` / `offZ` are metres (the imprint's finite-difference taps).
 */
export function deformDepthAt(wx: unknown, wz: unknown, offX = 0, offZ = 0): F {
  const b = deformBoundsNode as unknown as V4;
  const x = (wx as F).add(offX), z = (wz as F).add(offZ);
  const u = x.sub(b.x).div(b.z), v = z.sub(b.y).div(b.z);
  const inside = u.greaterThanEqual(0).and(u.lessThanEqual(1)).and(v.greaterThanEqual(0)).and(v.lessThanEqual(1));
  const vA = clamp(float(DEFORM_V0).add(v.mul(REGION_VH)), DEFORM_V0 + HALF_TEXEL_V, DEFORM_V0 + REGION_VH - HALF_TEXEL_V); // deformAtlasV(v)
  const s = (shelterTextureNode().sample(vec2(u, vA) as never) as unknown as { level: (l: unknown) => { r: F } }).level(float(0));
  return select(inside.and((deformEnabled as unknown as F).greaterThan(0.5)), s.r.max(0), float(0)) as unknown as F;
}
